import { getAssistantModelDescription } from './assistantModelDescriptions';
import { isModelPinned } from './assistantPinnedModels';

import type {
  GatewayModelInfo,
  GatewayModelParameterDefinition,
  GatewayModelParameterSelection,
  GatewayModelSelection,
} from '@/app/sdk';

export type AssistantModelListItem = {
  key: string;
  model: GatewayModelInfo;
  selection: GatewayModelSelection;
  baseLabel: string;
  optionSuffix: string | null;
  optionSuffixParts: string[];
};

export type ModelParameterGroups = {
  context?: GatewayModelParameterDefinition;
  effort?: GatewayModelParameterDefinition;
  thinking?: GatewayModelParameterDefinition;
  fast?: GatewayModelParameterDefinition;
  other: GatewayModelParameterDefinition[];
};

const TRAILING_OPTION_PATTERN = /^(.+?)\s+(high|medium|low|minimal|max|xhigh|fast|no thinking)$/i;

const CONTEXT_PARAM_IDS = new Set(['context', 'contextwindow', 'context_window', 'contextWindow']);
const EFFORT_PARAM_IDS = new Set(['effort', 'reasoning']);
const THINKING_PARAM_IDS = new Set(['thinking']);
const FAST_PARAM_IDS = new Set(['fast', 'speed']);
const OPTIONS_PARAM_IDS = new Set(['options', 'option']);

const OFF_VALUE_KEYS = new Set(['off', 'none', 'disabled', 'false', 'no', 'no-thinking', 'no_thinking']);
const EFFORT_VALUE_KEYS = new Set(['minimal', 'low', 'medium', 'high', 'xhigh', 'max', 'extra high', 'extra-high']);
const FAST_VALUE_KEYS = new Set(['fast', 'true', 'on', 'enabled']);
const ON_VALUE_KEYS = new Set(['on', 'true', 'enabled', 'yes']);

/** MAX mode labels stripped from selections; xhigh remains available when offered. */
const MAX_MODE_ONLY_KEYS = new Set(['max', 'maximum']);

export function isMaxModeOnlyLabel(value: string, displayName?: string): boolean {
  const labels = [value, displayName ?? ''].map(normaliseKey);
  return labels.some(label => MAX_MODE_ONLY_KEYS.has(label));
}

export function shouldShowParameterOption(
  option: { value: string; displayName?: string },
): boolean {
  return !isMaxModeOnlyLabel(option.value, option.displayName);
}

export function getDefaultSelectionForModel(model: GatewayModelInfo | undefined): GatewayModelSelection | undefined {
  if (!model) {
    return undefined;
  }

  const editorParams = buildEditorDefaultParams(model);
  const editorParamIds = new Set(editorParams.map(param => param.id));
  const variantParams = getDefaultVariantParameterSelections(model).filter(
    param => !editorParamIds.has(param.id),
  );
  const mergedParams = [...variantParams, ...editorParams];

  if (mergedParams.length === 0) {
    return sanitiseSelectionParams({ id: model.id }, model);
  }

  return sanitiseSelectionParams({ id: model.id, params: mergedParams }, model);
}

/** Fills missing params (e.g. fast off) without overriding explicit user choices. */
export function mergeSelectionWithEditorDefaults(
  selection: GatewayModelSelection,
  model: GatewayModelInfo | undefined,
): GatewayModelSelection {
  if (!model) {
    return selection;
  }

  const editorDefaults = buildEditorDefaultParams(model);
  if (editorDefaults.length === 0) {
    return sanitiseSelectionParams(selection, model);
  }

  const mergedById = new Map(
    (selection.params ?? []).map(param => [param.id, param] as const),
  );
  for (const editorParam of editorDefaults) {
    if (!mergedById.has(editorParam.id)) {
      mergedById.set(editorParam.id, editorParam);
    }
  }

  const mergedParams = [...mergedById.values()];
  return sanitiseSelectionParams(
    {
      id: selection.id,
      params: mergedParams.length > 0 ? mergedParams : undefined,
    },
    model,
  );
}

function getDefaultVariantParameterSelections(model: GatewayModelInfo): GatewayModelParameterSelection[] {
  const defaultVariant = model.variants?.find(variant => variant.isDefault)
    ?? model.variants?.[0];
  if (!defaultVariant?.params.length) {
    return [];
  }

  return defaultVariant.params.map(param => ({ id: param.id, value: param.value }));
}

function buildEditorDefaultParams(model: GatewayModelInfo): GatewayModelParameterSelection[] {
  const params: GatewayModelParameterSelection[] = [];
  const groups = groupModelParameters(model);

  if (groups.context) {
    const value = pickLowestContextValue(groups.context);
    if (value) {
      params.push({ id: groups.context.id, value });
    }
  }

  if (groups.effort) {
    const value = pickDefaultEffortValue(model, groups.effort);
    if (value) {
      params.push({ id: groups.effort.id, value });
    }
  } else if (groups.thinking) {
    const value = pickDefaultEffortValue(model, groups.thinking) ?? pickThinkingOnValue(groups.thinking);
    if (value) {
      params.push({ id: groups.thinking.id, value });
    }
  }

  if (groups.thinking && groups.effort) {
    const value = pickThinkingOnValue(groups.thinking);
    if (value) {
      params.push({ id: groups.thinking.id, value });
    }
  }

  if (groups.fast) {
    const value = pickFastOffValue(groups.fast);
    if (value) {
      params.push({ id: groups.fast.id, value });
    }
  }

  return params;
}

function pickLowestContextValue(parameter: GatewayModelParameterDefinition): string | undefined {
  const visible = parameter.values.filter(shouldShowParameterOption);
  if (visible.length === 0) {
    return parameter.values[0]?.value;
  }

  const scored = visible.map(option => ({
    option,
    score: parseContextNumeric(option.displayName ?? option.value),
  }));

  scored.sort((a, b) => a.score - b.score);
  return scored[0]?.option.value;
}

function parseContextNumeric(label: string): number {
  const normalised = label.toLowerCase().replace(/\s/g, '');
  const match = normalised.match(/(\d+(?:\.\d+)?)(k|m)?/);
  if (!match?.[1]) {
    return Number.MAX_SAFE_INTEGER;
  }
  const base = Number.parseFloat(match[1]);
  const unit = match[2];
  if (unit === 'm') {
    return base * 1_000_000;
  }
  if (unit === 'k') {
    return base * 1_000;
  }
  return base;
}

function modelPrefersHighEffort(model: GatewayModelInfo): boolean {
  const haystack = `${model.id} ${model.displayName}`.toLowerCase();
  return haystack.includes('opus');
}

function pickDefaultEffortValue(
  model: GatewayModelInfo,
  parameter: GatewayModelParameterDefinition,
): string | undefined {
  const visible = parameter.values.filter(shouldShowParameterOption);
  const targetLabel = modelPrefersHighEffort(model) ? 'high' : 'medium';

  const byLabel = visible.find(option => {
    const label = normaliseKey(option.displayName ?? option.value);
    return label === targetLabel;
  });
  if (byLabel) {
    return byLabel.value;
  }

  const nonOff = visible.find(option => !isOffValue(option.value, option.displayName));
  return nonOff?.value ?? visible[0]?.value;
}

function pickThinkingOnValue(parameter: GatewayModelParameterDefinition): string | undefined {
  const visible = parameter.values.filter(shouldShowParameterOption);
  const onOption = visible.find(option => {
    const label = normaliseKey(option.displayName ?? option.value);
    return ON_VALUE_KEYS.has(label) || (isEffortValue(option.value, option.displayName) && !isOffValue(option.value, option.displayName));
  });
  if (onOption) {
    return onOption.value;
  }
  const nonOff = visible.find(option => !isOffValue(option.value, option.displayName));
  return nonOff?.value;
}

function pickFastOffValue(parameter: GatewayModelParameterDefinition): string | undefined {
  const candidates = parameter.values.filter(shouldShowParameterOption);
  const values = candidates.length > 0 ? candidates : parameter.values;
  if (values.length === 0) {
    return undefined;
  }

  const explicitOff = values.find(option => isFastDisabled(option.value, option.displayName));
  if (explicitOff) {
    return explicitOff.value;
  }

  const notFast = values.find(option => !isFastEnabled(option.value, option.displayName));
  return notFast?.value ?? values[values.length - 1]?.value;
}

export function sanitiseSelectionParams(
  selection: GatewayModelSelection,
  model: GatewayModelInfo | undefined,
): GatewayModelSelection {
  if (!model?.parameters?.length) {
    return { id: selection.id };
  }

  const params = (selection.params ?? []).filter(param => {
    const definition = model.parameters?.find(item => item.id === param.id);
    if (!definition) {
      return false;
    }
    const option = definition.values.find(item => item.value === param.value);
    if (!option) {
      return false;
    }
    if (THINKING_PARAM_IDS.has(param.id.toLowerCase()) || EFFORT_PARAM_IDS.has(param.id.toLowerCase())) {
      return !isMaxModeOnlyLabel(option.value, option.displayName);
    }
    return true;
  }).map(param => ({ id: param.id, value: param.value }));

  return {
    id: selection.id,
    params: params.length > 0 ? params : undefined,
  };
}

export function splitModelDisplayName(displayName: string): { base: string; suffix: string | null } {
  const trimmed = displayName.trim();
  const match = trimmed.match(TRAILING_OPTION_PATTERN);
  if (match?.[1] && match[2]) {
    return { base: match[1].trim(), suffix: match[2].trim() };
  }
  return { base: trimmed, suffix: null };
}

export function getModelBaseLabel(model: GatewayModelInfo): string {
  return splitModelDisplayName(model.displayName).base;
}

function normaliseKey(value: string): string {
  return value.toLowerCase().replace(/[_-]/g, ' ').trim();
}

function findParameter(
  model: GatewayModelInfo,
  ids: Set<string>,
): GatewayModelParameterDefinition | undefined {
  return model.parameters?.find(
    param => ids.has(param.id) || ids.has(param.id.toLowerCase()),
  );
}

function isOffValue(value: string, displayName?: string): boolean {
  const labels = [value, displayName ?? ''].map(normaliseKey);
  return labels.some(label => OFF_VALUE_KEYS.has(label) || label === 'no thinking');
}

export function isFastDisabled(value: string, displayName?: string): boolean {
  const labels = [value, displayName ?? ''].map(normaliseKey);
  return labels.some(label => OFF_VALUE_KEYS.has(label) || label === 'false' || label === 'standard' || label === 'normal');
}

export function isFastEnabled(value: string, displayName?: string): boolean {
  if (isFastDisabled(value, displayName)) {
    return false;
  }

  const labels = [value, displayName ?? ''].map(normaliseKey);
  if (labels.some(label => FAST_VALUE_KEYS.has(label))) {
    return true;
  }

  return labels.some(label => label === 'fast' || label.endsWith('-fast') || label.endsWith(' fast'));
}

function isEffortValue(value: string, displayName?: string): boolean {
  if (isOffValue(value, displayName)) {
    return false;
  }
  const label = normaliseKey(displayName ?? value);
  return EFFORT_VALUE_KEYS.has(label);
}

function getEditorDefaultParameterValue(
  model: GatewayModelInfo,
  parameter: GatewayModelParameterDefinition,
): string {
  const defaults = getDefaultSelectionForModel(model);
  if (!defaults) {
    return parameter.values[0]?.value ?? '';
  }
  return getParameterValue(defaults, parameter);
}

function getContextSuffixPart(
  selection: GatewayModelSelection,
  model: GatewayModelInfo,
): string | null {
  const parameter = findParameter(model, CONTEXT_PARAM_IDS);
  if (!parameter) {
    return null;
  }
  const current = getParameterValue(selection, parameter);
  const defaultValue = getEditorDefaultParameterValue(model, parameter);
  if (!current || current === defaultValue) {
    return null;
  }
  return getParameterDisplayValue(parameter, current);
}

function getEffortSuffixPart(
  selection: GatewayModelSelection,
  model: GatewayModelInfo,
): string | null {
  const effortParameter = findParameter(model, EFFORT_PARAM_IDS);
  if (effortParameter) {
    const current = getParameterValue(selection, effortParameter);
    if (isEffortValue(current, getParameterDisplayValue(effortParameter, current) ?? undefined)) {
      return getParameterDisplayValue(effortParameter, current);
    }
  }

  const thinkingParameter = findParameter(model, THINKING_PARAM_IDS);
  if (!thinkingParameter) {
    return null;
  }
  const current = getParameterValue(selection, thinkingParameter);
  if (isOffValue(current, getParameterDisplayValue(thinkingParameter, current) ?? undefined)) {
    return null;
  }
  if (isEffortValue(current, getParameterDisplayValue(thinkingParameter, current) ?? undefined)) {
    return getParameterDisplayValue(thinkingParameter, current);
  }
  return null;
}

function getNoThinkingSuffixPart(
  selection: GatewayModelSelection,
  model: GatewayModelInfo,
): string | null {
  const thinkingParameter = findParameter(model, THINKING_PARAM_IDS);
  if (!thinkingParameter) {
    return null;
  }
  const supportsOff = thinkingParameter.values.some(
    option => isOffValue(option.value, option.displayName),
  );
  if (!supportsOff) {
    return null;
  }
  const current = getParameterValue(selection, thinkingParameter);
  if (!isOffValue(current, getParameterDisplayValue(thinkingParameter, current) ?? undefined)) {
    return null;
  }
  return 'No Thinking';
}

function getOptionsSuffixPart(
  selection: GatewayModelSelection,
  model: GatewayModelInfo,
): string | null {
  const fastParameter = findParameter(model, FAST_PARAM_IDS);
  if (fastParameter) {
    const current = getParameterValue(selection, fastParameter);
    if (isFastEnabled(current, getParameterDisplayValue(fastParameter, current) ?? undefined)) {
      return getParameterDisplayValue(fastParameter, current) ?? 'Fast';
    }
  }

  const optionsParameter = findParameter(model, OPTIONS_PARAM_IDS);
  if (!optionsParameter) {
    return null;
  }
  const current = getParameterValue(selection, optionsParameter);
  const display = getParameterDisplayValue(optionsParameter, current);
  if (isFastEnabled(current, display ?? undefined)) {
    return display ?? 'Fast';
  }
  return null;
}

export function getModelOptionSuffixParts(
  selection: GatewayModelSelection,
  model: GatewayModelInfo,
): string[] {
  const parts: [
    string | null,
    string | null,
    string | null,
    string | null,
  ] = [
    getContextSuffixPart(selection, model),
    getEffortSuffixPart(selection, model),
    getNoThinkingSuffixPart(selection, model),
    getOptionsSuffixPart(selection, model),
  ];
  return parts.filter((part): part is string => !!part);
}

export function getModelOptionSuffix(
  selection: GatewayModelSelection,
  model: GatewayModelInfo,
): string | null {
  const parts = getModelOptionSuffixParts(selection, model);
  return parts.length > 0 ? parts.join(' ') : null;
}

export function formatModelTriggerLabel(
  selection: GatewayModelSelection,
  model: GatewayModelInfo | undefined,
): string {
  if (!model) {
    return selection.id;
  }
  const base = getModelBaseLabel(model);
  const suffix = getModelOptionSuffix(selection, model);
  return suffix ? `${base} ${suffix}` : base;
}

export function groupModelParameters(model: GatewayModelInfo): ModelParameterGroups {
  const other: GatewayModelParameterDefinition[] = [];
  const groups: ModelParameterGroups = { other };

  for (const parameter of model.parameters ?? []) {
    const id = parameter.id.toLowerCase();
    if (CONTEXT_PARAM_IDS.has(id)) {
      groups.context = parameter;
    } else if (EFFORT_PARAM_IDS.has(id)) {
      groups.effort = parameter;
    } else if (THINKING_PARAM_IDS.has(id)) {
      groups.thinking = parameter;
    } else if (FAST_PARAM_IDS.has(id)) {
      groups.fast = parameter;
    } else if (OPTIONS_PARAM_IDS.has(id)) {
      groups.fast ??= parameter;
      other.push(parameter);
    } else {
      other.push(parameter);
    }
  }

  return groups;
}

export function modelHasConfigurableOptions(model: GatewayModelInfo): boolean {
  const groups = groupModelParameters(model);
  if (groups.fast) {
    return true;
  }
  const sectionParams = [
    groups.context,
    groups.effort,
    groups.thinking,
    ...groups.other,
  ].filter((param): param is GatewayModelParameterDefinition => !!param);

  return sectionParams.some(
    parameter => parameter.values.some(shouldShowParameterOption),
  );
}

export type AssistantModelTooltipFacts = {
  description: string;
  contextLine: string | null;
  lines: string[];
};

export function buildContextTooltipLine(
  model: GatewayModelInfo,
  selection: GatewayModelSelection,
): string | null {
  const groups = groupModelParameters(model);

  if (groups.context) {
    const display = getParameterDisplayValue(
      groups.context,
      getParameterValue(selection, groups.context, model),
    );
    if (display) {
      return `${display} context window`;
    }
  }

  const fixedTokens = model.contextWindowTokens;
  if (typeof fixedTokens === 'number' && fixedTokens > 0) {
    return `${formatContextWindowTokens(fixedTokens)} context window`;
  }

  return null;
}

export function buildAssistantModelTooltipFacts(
  model: GatewayModelInfo,
  selection: GatewayModelSelection,
): AssistantModelTooltipFacts {
  const lines: string[] = [];
  const contextLine = buildContextTooltipLine(model, selection);
  const groups = groupModelParameters(model);

  const effortParameter = groups.effort ?? (groups.thinking && isEffortThinkingParam(groups.thinking) ? groups.thinking : undefined);
  if (effortParameter) {
    const value = getParameterValue(selection, effortParameter);
    const display = getParameterDisplayValue(effortParameter, value);
    if (display && isEffortValue(value, display)) {
      lines.push(`Reasoning: ${display}`);
    } else if (isOffValue(value, display ?? undefined)) {
      lines.push('Thinking: off');
    }
  } else if (groups.thinking) {
    const value = getParameterValue(selection, groups.thinking);
    const display = getParameterDisplayValue(groups.thinking, value);
    if (isOffValue(value, display ?? undefined)) {
      lines.push('Thinking: off');
    } else if (display) {
      lines.push(`Thinking: ${display}`);
    }
  }

  if (groups.fast) {
    const value = getParameterValue(selection, groups.fast);
    const display = getParameterDisplayValue(groups.fast, value);
    if (isFastEnabled(value, display ?? undefined)) {
      lines.push('Fast: on');
    }
  }

  return {
    description: getAssistantModelDescription(model),
    contextLine,
    lines,
  };
}

function formatContextWindowTokens(tokens: number): string {
  if (tokens >= 1_000_000) {
    const millions = tokens / 1_000_000;
    return Number.isInteger(millions) ? `${millions}M` : `${millions.toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    const thousands = tokens / 1_000;
    return Number.isInteger(thousands) ? `${thousands}k` : `${thousands.toFixed(1)}k`;
  }
  return tokens.toLocaleString();
}

function isEffortThinkingParam(parameter: GatewayModelParameterDefinition): boolean {
  return parameter.values.some(
    option => isEffortValue(option.value, option.displayName),
  );
}

export function buildModelListItem(
  model: GatewayModelInfo,
  selectionForModel: GatewayModelSelection,
): AssistantModelListItem {
  const suffixParts = getModelOptionSuffixParts(selectionForModel, model);
  return {
    key: model.id,
    model,
    selection: selectionForModel,
    baseLabel: getModelBaseLabel(model),
    optionSuffix: suffixParts.length > 0 ? suffixParts.join(' ') : null,
    optionSuffixParts: suffixParts,
  };
}

export function buildPinnedModelListItems(
  models: GatewayModelInfo[],
  pinnedIds: string[],
  activeSelection: GatewayModelSelection,
): AssistantModelListItem[] {
  const byId = new Map(models.map(model => [model.id, model]));
  const items: AssistantModelListItem[] = [];

  for (const id of pinnedIds) {
    const model = byId.get(id);
    if (!model) {
      continue;
    }
    const selection = activeSelection.id === model.id
      ? sanitiseSelectionParams(activeSelection, model)
      : (getDefaultSelectionForModel(model) ?? { id: model.id });
    items.push(buildModelListItem(model, selection));
  }

  return sortAssistantModelListItemsByCatalogueOrder(items);
}

function isComposerModelId(modelId: string): boolean {
  return modelId.toLowerCase().includes('composer');
}

/** Composer models first, then remaining models sorted by catalogue id. */
export function compareAssistantModelListItemsByCatalogueOrder(
  a: AssistantModelListItem,
  b: AssistantModelListItem,
): number {
  const aComposer = isComposerModelId(a.model.id);
  const bComposer = isComposerModelId(b.model.id);
  if (aComposer !== bComposer) {
    return aComposer ? -1 : 1;
  }
  return a.model.id.localeCompare(b.model.id, undefined, { sensitivity: 'base' });
}

export function sortAssistantModelListItemsByCatalogueOrder(
  items: AssistantModelListItem[],
): AssistantModelListItem[] {
  return [...items].sort(compareAssistantModelListItemsByCatalogueOrder);
}

export function buildAllModelListItems(
  models: GatewayModelInfo[],
  activeSelection: GatewayModelSelection,
): AssistantModelListItem[] {
  return models.map(model => {
    const selection = activeSelection.id === model.id
      ? sanitiseSelectionParams(activeSelection, model)
      : (getDefaultSelectionForModel(model) ?? { id: model.id });
    return buildModelListItem(model, selection);
  });
}

/** Models omitted from the full model-list dialog (legacy entries kept in the catalogue). */
const MODEL_LIST_HIDDEN_IDS = new Set(['composer-2']);

export function isModelHiddenFromModelList(model: GatewayModelInfo): boolean {
  return MODEL_LIST_HIDDEN_IDS.has(model.id);
}

export function sortModelListDialogItems(
  items: AssistantModelListItem[],
  enabledModelIds: string[],
): AssistantModelListItem[] {
  return items
    .filter(item => !isModelHiddenFromModelList(item.model))
    .sort((a, b) => {
      const aEnabled = isModelPinned(enabledModelIds, a.model.id);
      const bEnabled = isModelPinned(enabledModelIds, b.model.id);
      if (aEnabled !== bEnabled) {
        return aEnabled ? -1 : 1;
      }
      return compareAssistantModelListItemsByCatalogueOrder(a, b);
    });
}

export function filterModelListItems(items: AssistantModelListItem[], search: string): AssistantModelListItem[] {
  const keywords = search.toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (keywords.length === 0) {
    return items;
  }

  return items.filter(item => {
    const tooltip = buildAssistantModelTooltipFacts(item.model, item.selection);
    const haystack = [
      item.baseLabel,
      item.optionSuffix ?? '',
      item.optionSuffixParts.join(' '),
      tooltip.description,
      tooltip.contextLine ?? '',
      tooltip.lines.join(' '),
      item.model.id,
    ].join(' ').toLowerCase();
    return keywords.every(keyword => haystack.includes(keyword));
  });
}

export function getParameterValue(
  selection: GatewayModelSelection,
  parameter: GatewayModelParameterDefinition,
  model?: GatewayModelInfo,
): string {
  const selectedValue = selection.params?.find(item => item.id === parameter.id)?.value;
  if (selectedValue && parameter.values.some(option => option.value === selectedValue)) {
    return selectedValue;
  }
  if (model) {
    return getEditorDefaultParameterValue(model, parameter);
  }
  return parameter.values[0]?.value ?? '';
}

export function getParameterDisplayValue(
  parameter: GatewayModelParameterDefinition,
  value: string,
): string | null {
  if (!value) {
    return null;
  }
  return parameter.values.find(option => option.value === value)?.displayName ?? value;
}

export function withUpdatedParameter(
  params: GatewayModelSelection['params'],
  parameterId: string,
  value: string,
): NonNullable<GatewayModelSelection['params']> {
  const nextParams = [...(params ?? [])].filter(param => param.id !== parameterId);
  nextParams.push({ id: parameterId, value });
  return nextParams;
}

/** @deprecated Use getDefaultSelectionForModel */
export function getDefaultPresetForModel(model: GatewayModelInfo | undefined): GatewayModelSelection | undefined {
  return getDefaultSelectionForModel(model);
}